import {
  ApolloError,
  NetworkStatus,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@apollo/client";
import {
  AccountCard,
  Alert,
  BreadcrumbArray,
  Button,
  Card,
  ConnectedAnnotatedField,
  Copyable,
  CurrencyAmountUI,
  CurrencyFlagEmoji,
  cn,
  DataTable,
  DateUI,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  ErrorTooltip,
  Icon,
  Label,
  LabelPill,
  LoadingToastProvider,
  MfaFlow,
  Number as NumberUI,
  NumericFormatInput,
  PrimaryFullScreenSpinner,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Txt,
  toast,
  UserProfile,
  useAppForm,
} from "@repo/ui";
import { AccountList } from "@repo/ui/blocks";
import {
  arrayBufferToBase64,
  dateSchema,
  decimalSchema,
  isCurrency,
  NotFoundError,
  neverFunc,
  prettyPrintEnum,
} from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type Decimal from "decimal.js";
import { type ReactNode, Suspense, useRef, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import {
  PiArrowsClockwise,
  PiBuildingOffice,
  PiCheckCircle,
  PiClipboard,
  PiMailbox,
  PiNotePencil,
  PiPlug,
  PiPlugsConnected,
  PiPlus,
  PiRobot,
  PiUser,
  PiX,
} from "react-icons/pi";
import {
  RiArrowDownSLine,
  RiBankLine,
  RiExternalLinkLine,
} from "react-icons/ri";
import { z } from "zod";
import loadingAnimation from "~/assets/animations/loading.riv";
import { InlineSaveStatus } from "~/components/InlineSaveStatus";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { graphql } from "~/gql/client";
import {
  activityStatementFormatValues,
  currencyValues,
  organisationApprovalPolicyKindValues,
  organisationFeePaymentMethodValues,
  userMfaRequirementValues,
} from "~/gql/client/enumValues";
import type {
  ActivityStatementFormat,
  AdminOrganisationApprovalPolicyFieldsFragment,
  AdminOrganisationQuery,
  BeneficiaryAssetCardFieldsFragment,
  OrgAssetRuleRowFragment,
  OrganisationApprovalPolicyKind,
  UpdateOrganisationDetailInput,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/organisations_/$organisationId")({
  head: () => opsHead("Organisation"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <OrganisationLayout />
    </Suspense>
  ),
});

const assetRuleColumns: ColumnDef<OrgAssetRuleRowFragment>[] = [
  {
    id: "name",
    header: "Name",
    cell: ({ row }) => <Txt variant="name">{row.original.name}</Txt>,
  },
  {
    id: "account",
    header: "Account",
    cell: ({ row }) => {
      if (row.original.asset) {
        return (
          <AccountCard
            account={row.original.asset}
            subText="BUSINESS_ENTITY"
            linkTo={`/assets/${row.original.asset.id}`}
          />
        );
      }
      if (
        row.original.__typename === "RecurringWithdrawalAssetRule" &&
        row.original.beneficiaryAccount
      ) {
        return (
          <AccountCard
            account={
              row.original
                .beneficiaryAccount as BeneficiaryAssetCardFieldsFragment
            }
            subText="BUSINESS_ENTITY"
          />
        );
      }
      return "—";
    },
  },
  {
    id: "type",
    header: "Type",
    cell: ({ row }) => {
      switch (row.original.__typename) {
        case "TopUpAssetRule":
          return "Top-up";
        case "AlertAssetRule":
          return "Alert";
        case "RecurringTransferAssetRule":
          return "Recurring transfer";
        case "RecurringWithdrawalAssetRule":
          return "Recurring withdrawal";
        case "SweepAssetRule":
          return "Auto sweep";
        default:
          return neverFunc(row.original);
      }
    },
  },
  {
    id: "amount",
    header: "Amount",
    cell: ({ row }) => {
      const rule = row.original;
      if (rule.__typename === "TopUpAssetRule" && rule.balanceTarget) {
        return (
          <div className="flex flex-col">
            <Txt variant="caption" tone="secondary">
              Target
            </Txt>
            <CurrencyAmountUI
              currency={rule.balanceTarget.currency}
              amount={rule.balanceTarget.amount}
            />
          </div>
        );
      }
      if (rule.__typename === "AlertAssetRule") {
        return (
          <div className="flex flex-col gap-0.5">
            {rule.minimumBalanceThreshold && (
              <div className="flex flex-col">
                <Txt variant="caption" tone="secondary">
                  Min
                </Txt>
                <CurrencyAmountUI
                  currency={rule.minimumBalanceThreshold.currency}
                  amount={rule.minimumBalanceThreshold.amount}
                />
              </div>
            )}
            {rule.maximumBalanceThreshold && (
              <div className="flex flex-col">
                <Txt variant="caption" tone="secondary">
                  Max
                </Txt>
                <CurrencyAmountUI
                  currency={rule.maximumBalanceThreshold.currency}
                  amount={rule.maximumBalanceThreshold.amount}
                />
              </div>
            )}
          </div>
        );
      }
      if (rule.__typename === "RecurringWithdrawalAssetRule" && rule.amount) {
        return (
          <CurrencyAmountUI
            currency={rule.amount.currency}
            amount={rule.amount.amount}
          />
        );
      }
      if (rule.__typename === "RecurringTransferAssetRule" && rule.amount) {
        if (isCurrency(rule.amount.unit)) {
          return (
            <CurrencyAmountUI
              currency={rule.amount.unit}
              amount={rule.amount.amount}
            />
          );
        }
        return (
          <NumberUI
            value={rule.amount.amount}
            format={NumberUI.format.units(rule.amount.amount)}
          />
        );
      }
      if (rule.__typename === "SweepAssetRule" && rule.balanceTarget) {
        return (
          <div className="flex flex-col">
            <Txt variant="caption" tone="secondary">
              Target
            </Txt>
            <CurrencyAmountUI
              currency={rule.balanceTarget.currency}
              amount={rule.balanceTarget.amount}
            />
          </div>
        );
      }
      return "—";
    },
  },
  {
    id: "status",
    header: "Eval Status",
    cell: ({ row }) =>
      row.original.evaluationStatus
        ? prettyPrintEnum(row.original.evaluationStatus)
        : "—",
  },
  {
    id: "cadence",
    header: "Cadence",
    cell: ({ row }) => prettyPrintEnum(row.original.evaluationCadence),
  },
  {
    id: "nextEvaluation",
    header: "Next evaluation",
    cell: ({ row }) => {
      if (row.original.status === "INACTIVE") {
        return <LabelPill label="Inactive" />;
      }
      return <DateUI date={row.original.nextEvaluationDueAt} />;
    },
  },
];

const approvalPolicyFormSchema = z.object({
  kind: z.enum(organisationApprovalPolicyKindValues),
  numUserReviewsRequired: z.enum(["0", "1", "2", "3", "4"] as const, {
    error: "Select a value between 0 and 4",
  }),
  allowSelfReview: z.boolean(),
  allowPolicyOverride: z.boolean(),
});

type ApprovalPolicyFormValues = z.input<typeof approvalPolicyFormSchema>;

type ApprovalPolicy = {
  kind: OrganisationApprovalPolicyKind;
  approvalPolicy:
    | AdminOrganisationApprovalPolicyFieldsFragment
    | null
    | undefined;
};

const makeFormSchema = (
  organisation: NonNullable<AdminOrganisationQuery["organisation"]>,
) =>
  z
    .object({
      activityStatementFormat: z.enum(activityStatementFormatValues),
      fixedMonthlyFeeAmount: decimalSchema.optional(),
      fxMarginRateBps: decimalSchema.optional(),
      fixedMonthlyFeeCurrency: z.enum(currencyValues).optional(),
      fixedMonthlyFeeContractEndDate: dateSchema.optional(),
      userMfaRequirement: z.enum(userMfaRequirementValues),
      homeCurrency: z.enum(currencyValues),
      xeroContactId: z.string(),
      feePaymentMethod: z.enum(organisationFeePaymentMethodValues),
      longLogo: z
        .instanceof(File)
        .nullish()
        .refine((file) => !file || file.type === "image/png", {
          message: "Only PNG files are allowed",
        })
        .refine((file) => !file || file.size <= 2 * 1024 * 1024, {
          message: "File must be less than 2MB",
        }),
    })
    .superRefine((data, ctx) => {
      if (data.fixedMonthlyFeeAmount && !data.fixedMonthlyFeeCurrency) {
        ctx.addIssue({
          path: ["fixedMonthlyFeeCurrency"],
          code: "custom",
          message: "Currency is required",
        });
      }

      if (!data.fixedMonthlyFeeAmount && data.fixedMonthlyFeeCurrency) {
        ctx.addIssue({
          path: ["fixedMonthlyFeeAmount"],
          code: "custom",
          message: "Amount is required",
        });
      }
    })
    .superRefine((value, ctx) => {
      if (value.activityStatementFormat !== "NETSUITE") {
        return;
      }

      const businessEntitiesMissingNetsuiteIds =
        organisation.businessEntities?.filter(
          (entity) => !entity.netsuiteInternalCustomerId?.trim(),
        );

      if (
        businessEntitiesMissingNetsuiteIds &&
        businessEntitiesMissingNetsuiteIds?.length > 0
      ) {
        ctx.addIssue({
          path: ["activityStatementFormat"],
          code: "custom",
          message: `You must set a NetSuite ID on all business entities in this organisation. ${businessEntitiesMissingNetsuiteIds.map(({ name }) => name).join(", ")} ${businessEntitiesMissingNetsuiteIds.length === 1 ? "is" : "are"} currently missing ${businessEntitiesMissingNetsuiteIds.length === 1 ? "a NetSuite ID" : "NetSuite IDs"}.`,
        });
      }
    });

type FormSchema = z.input<ReturnType<typeof makeFormSchema>>;

const notificationsDocument = graphql(`
  query AdminOrganisationNotifications($id: ID!, $after: String, $first: Int!) {
    organisation(id: $id) {
      notifications(after: $after, first: $first) {
        pageInfo {
          endCursor
          hasNextPage
        }
        notifications {
          id
          createdAt
          kind
          recoveryPoint
        }
      }
    }
  }
`);

const NOTIFICATIONS_PAGE_SIZE = 20;

function OrganisationLayout() {
  const params = useParams({ strict: false });
  const { organisationId } = params;
  if (!organisationId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminOrganisation($id: ID!) {
        organisation(id: $id) {
          id
          shortRef
          name
          activityStatementFormat
          estimatedValue {
            currency
            amount
          }
          homeCurrency
          createdAt
          squareLogoUrl
          fixedMonthlyFee {
            currency
            amount
          }
          fixedMonthlyFeeContractEndDate
          userMfaRequirement
          hasLongLogo
          longLogoDataUrl
          xeroContact {
            id
          }
          pendingExternalAccountLinks {
            id
            provider
          }
          xeroConnection {
            id
            status
            lastSyncedAt
          }
          businessEntities {
            id
            name
            currency
            netsuiteInternalCustomerId
            assets(includeInactive: true) {
              id
              active
              ...AssetCardFields
            }
            activeAirwallexConnections {
              id
              clientId
              accountName
              status
            }
            activeAkahuConnection {
              id
            }
            activeSaltEdgeConnections {
              id
              providerName
            }
            skriptConnection {
              id
              skriptId
              arrangements {
                id
                status
                accounts {
                  id
                  name
                  asset {
                    id
                    name
                    estimatedValue {
                      amount
                      currency
                    }
                    createdAt
                  }
                }
              }
            }
            currencycloudAccount {
              id
              accountName
              status
            }
            eburyClient {
              id
              clientName
            }
            externalAccounts {
              __typename
              id
              ... on AirwallexExternalAccountDetails {
                awxConn: connection {
                  id
                }
                name
                asset {
                  id
                  name
                  estimatedValue {
                    amount
                    currency
                  }
                  createdAt
                }
              }
              ... on AkahuExternalAccountDetails {
                asset {
                  id
                  name
                  estimatedValue {
                    amount
                    currency
                  }
                  createdAt
                }
              }
              ... on SaltEdgeExternalAccountDetails {
                seConn: connection {
                  id
                }
                name
                asset {
                  id
                  name
                  estimatedValue {
                    amount
                    currency
                  }
                  createdAt
                }
              }
              ... on QuilttExternalAccountDetails {
                qConn: connection {
                  id
                }
                providerFriendlyName
                name
                asset {
                  id
                  name
                  estimatedValue {
                    amount
                    currency
                  }
                  createdAt
                }
              }
              ... on StripeExternalAccountDetails {
                stConn: connection {
                  id
                  stripeAccountId
                  status
                }
                name
                asset {
                  id
                  name
                  estimatedValue {
                    amount
                    currency
                  }
                  createdAt
                }
              }
              ... on MercuryExternalAccountDetails {
                mConn: connection {
                  id
                }
                name
                asset {
                  id
                  name
                  estimatedValue {
                    amount
                    currency
                  }
                  createdAt
                }
              }
              ... on BrexExternalAccountDetails {
                bConn: connection {
                  id
                }
                name
                asset {
                  id
                  name
                  estimatedValue {
                    amount
                    currency
                  }
                  createdAt
                }
              }
              ... on RampExternalAccountDetails {
                rConn: connection {
                  id
                }
                name
                asset {
                  id
                  name
                  estimatedValue {
                    amount
                    currency
                  }
                  createdAt
                }
              }
            }
          }
          feePaymentMethod
          fxMarginRateBps
          payoutApprovalPolicy {
            ...AdminOrganisationApprovalPolicyFields
          }
          transferApprovalPolicy {
            ...AdminOrganisationApprovalPolicyFields
          }
          thirdPartyPaymentApprovalPolicy {
            ...AdminOrganisationApprovalPolicyFields
          }
          memberships {
            id
            userProfile {
              id
              ...UserProfileUI
              notificationsEnabled
            }
          }
          assetRulesSummary {
            id
            activeAssetRules {
              ...OrgAssetRuleRow
            }
          }
        }
      }

      fragment OrgAssetRuleRow on AssetRule {
        __typename
        id
        name
        evaluationCadence
        evaluationStatus
        nextEvaluationDueAt
        status
        asset {
          id
          ...AssetCardFields
        }
        ... on TopUpAssetRule {
          balanceTarget {
            currency
            amount
          }
        }
        ... on AlertAssetRule {
          minimumBalanceThreshold {
            currency
            amount
          }
          maximumBalanceThreshold {
            currency
            amount
          }
        }
        ... on RecurringTransferAssetRule {
          amount {
            amount
            unit
          }
        }
        ... on RecurringWithdrawalAssetRule {
          amount {
            currency
            amount
          }
          beneficiaryAccount {
            ...BeneficiaryAssetCardFields
          }
        }
        ... on SweepAssetRule {
          balanceTarget {
            currency
            amount
          }
        }
      }

      fragment AdminOrganisationApprovalPolicyFields on ApprovalPolicy {
        id
        allowPolicyOverride
        allowSelfReview
        numUserReviewsRequired
      }

    `),
    {
      variables: {
        id: organisationId,
      },
    },
  );

  const organisation = data.organisation;
  if (!organisation) {
    throw new NotFoundError();
  }

  useOpsTitle(`${organisation.name} - Organisation`);

  const [editDetailsCollapsed, setEditDetailsCollapsed] = useState(true);
  const [businessEntitiesCollapsed, setBusinessEntitiesCollapsed] =
    useState(false);
  const [usersCollapsed, setUsersCollapsed] = useState(true);
  const notificationLoadingUserIdRef = useRef<string | null>(null);

  const [approvalsCollapsed, setApprovalsCollapsed] = useState(true);
  const [rulesCollapsed, setRulesCollapsed] = useState(true);
  const [externalConnectionsCollapsed, setExternalConnectionsCollapsed] =
    useState(true);
  const [notificationsCollapsed, setNotificationsCollapsed] = useState(true);

  const {
    data: notificationsData,
    loading: notificationsLoading,
    networkStatus: notificationsNetworkStatus,
    fetchMore: fetchMoreNotifications,
  } = useQuery(notificationsDocument, {
    variables: {
      id: organisationId,
      after: null,
      first: NOTIFICATIONS_PAGE_SIZE,
    },
    skip: notificationsCollapsed,
    notifyOnNetworkStatusChange: true,
  });

  const notifications =
    notificationsData?.organisation?.notifications?.notifications ?? [];
  const notificationsNextCursor = notificationsData?.organisation?.notifications
    ?.pageInfo.hasNextPage
    ? (notificationsData.organisation.notifications.pageInfo.endCursor ??
      undefined)
    : undefined;

  const defaultValues: FormSchema = {
    activityStatementFormat: organisation.activityStatementFormat ?? "XERO",
    userMfaRequirement: organisation.userMfaRequirement,
    fixedMonthlyFeeAmount: organisation.fixedMonthlyFee?.amount,
    fxMarginRateBps: organisation.fxMarginRateBps,
    fixedMonthlyFeeCurrency: organisation.fixedMonthlyFee?.currency,
    fixedMonthlyFeeContractEndDate:
      organisation.fixedMonthlyFeeContractEndDate ?? undefined,
    homeCurrency: organisation.homeCurrency,
    xeroContactId: organisation.xeroContact?.id ?? "",
    feePaymentMethod: organisation.feePaymentMethod,
  };
  const navigate = useNavigate();

  const [updateOrganisationDetail, { loading }] = useMutation(
    graphql(`
      mutation UpdateOrganisationDetail($input: UpdateOrganisationDetailInput!) {
        updateOrganisationDetail(input: $input) {
          organisation {
            id
            activityStatementFormat
            userMfaRequirement
            homeCurrency
            fixedMonthlyFeeContractEndDate
            fixedMonthlyFee {
              currency
              amount
            }
            hasLongLogo
            feePaymentMethod
            xeroContact {
              id
            }
            fxMarginRateBps
          }
        }
      }
    `),
  );

  const [deactivateOrganisation, { loading: deactivateLoading }] = useMutation(
    graphql(`
      mutation DeactivateOrganisation($input: DeactivateOrganisationInput!) {
        deactivateOrganisation(input: $input) {
          organisation {
            id
          }
        }
      }
    `),
  );
  const [setOrganisationLongLogo] = useMutation(
    graphql(`
      mutation SetOrganisationLongLogo($input: SetOrganisationLongLogoInput!) {
        setOrganisationLongLogo(input: $input) {
          organisation {
            id
            hasLongLogo
            longLogoDataUrl
          }
        }
      }
    `),
  );

  const [setUserNotificationsEnabled, notificationMutationResult] = useMutation(
    graphql(`
      mutation SetUserNotificationsEnabled($input: SetUserNotificationsEnabledInput!) {
        setUserNotificationsEnabled(input: $input) {
          userProfile {
            id
            notificationsEnabled
          }
        }
      }
    `),
  );

  const formSchema = makeFormSchema(organisation);
  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error updating details",
        });
        return;
      }

      const input: UpdateOrganisationDetailInput = {
        organisationId: organisation.id,
        activityStatementFormat: parsed.data.activityStatementFormat,
        userMfaRequirement: parsed.data.userMfaRequirement,
        homeCurrency: parsed.data.homeCurrency,
        fixedMonthlyFeeContractEndDate:
          parsed.data.fixedMonthlyFeeContractEndDate,
        xeroContactId: parsed.data.xeroContactId,
        feePaymentMethod: parsed.data.feePaymentMethod,
        fxMarginRateBps: parsed.data.fxMarginRateBps,
      };
      if (
        parsed.data.fixedMonthlyFeeAmount &&
        parsed.data.fixedMonthlyFeeCurrency
      ) {
        input.fixedMonthlyFee = {
          amount: parsed.data.fixedMonthlyFeeAmount,
          currency: parsed.data.fixedMonthlyFeeCurrency,
        };
      }

      try {
        await Promise.all([
          updateOrganisationDetail({
            variables: {
              input,
            },
          }),
          (async (): Promise<void> => {
            if (parsed.data.longLogo === undefined) {
              return;
            }

            await setOrganisationLongLogo({
              variables: {
                input: {
                  organisationId: organisation.id,
                  logoLongPngOnLightBase64:
                    parsed.data.longLogo === null
                      ? null
                      : arrayBufferToBase64(
                          await parsed.data.longLogo.arrayBuffer(),
                        ),
                },
              },
            });
          })(),
        ]);

        toast({
          kind: "success",
          autoHide: true,
          title: "Updated details",
        });
      } catch (e) {
        if (e instanceof ApolloError) {
          const errorMessage = e.graphQLErrors?.[0]?.message;
          toast({
            kind: "error",
            autoHide: false,
            title: "Error updating details",
            description: errorMessage,
          });
        } else {
          toast({
            kind: "error",
            autoHide: false,
            title: "Error updating details",
          });
        }
      }
    },
  });

  const orgIdRef = useRef(organisation.id);
  if (organisation.id !== orgIdRef.current) {
    orgIdRef.current = organisation.id;
    form.reset(defaultValues);
  }

  const [previewUrl, setPreviewUrl] = useState<string | null>(
    organisation.longLogoDataUrl ?? null,
  );

  const longLogoRef = useRef<HTMLInputElement | null>(null);

  const handleDeactivateOrganisation = async () => {
    try {
      await deactivateOrganisation({
        variables: {
          input: {
            organisationId: organisation.id,
          },
        },
      });
      toast({
        kind: "success",
        autoHide: true,
        title: "Organisation deactivated",
      });
      await navigate({ to: "/organisations" });
    } catch (e) {
      if (e instanceof ApolloError) {
        const errorMessage = e.graphQLErrors?.[0]?.message;
        toast({
          kind: "error",
          autoHide: false,
          title: "Error deactivating organisation",
          description: errorMessage,
        });
      } else {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error deactivating organisation",
        });
      }
    }
  };

  const policyDefinitions: ApprovalPolicy[] = [
    {
      kind: "PAYOUTS",
      approvalPolicy: data?.organisation?.payoutApprovalPolicy ?? null,
    },
    {
      kind: "TRANSFERS",
      approvalPolicy: data?.organisation?.transferApprovalPolicy ?? null,
    },
    {
      kind: "THIRD_PARTY_PAYMENTS",
      approvalPolicy:
        data?.organisation?.thirdPartyPaymentApprovalPolicy ?? null,
    },
  ];
  return (
    <AdminPageContainer>
      <div className="mt-8 flex flex-col gap-4">
        <div className="mb-6 flex flex-row items-center gap-2">
          <Icon
            size="10"
            imageUrl={organisation.squareLogoUrl ?? undefined}
            fallbackIcon={<PiBuildingOffice />}
          />
          <Txt variant="page-title">{organisation.name}</Txt>
        </div>
        <div className="flex w-full flex-row gap-6">
          <Card className="flex-[1] p-8">
            <div className="mb-6 flex flex-row items-center gap-2">
              <PiClipboard />
              <Txt variant="card-title">Summary</Txt>
            </div>
            <div className="flex flex-row items-start justify-between">
              <div className="flex flex-col gap-1">
                <Txt variant="detail-label">Balance</Txt>
                <Txt variant="metric">
                  {organisation.estimatedValue ? (
                    <CurrencyAmountUI {...organisation.estimatedValue} />
                  ) : (
                    "-"
                  )}
                </Txt>
              </div>
              <div className="flex flex-col gap-1">
                <Txt variant="detail-label">Signup Date</Txt>
                <Txt variant="metric">
                  <DateUI date={organisation.createdAt} />
                </Txt>
              </div>
              <div className="flex flex-col gap-1">
                <Txt variant="detail-label">ID</Txt>
                <Txt variant="metric">
                  <Copyable copyText={organisation.id}>
                    {organisation.shortRef}
                  </Copyable>
                </Txt>
              </div>
              <a
                href={`https://dashboard.clerk.com/apps/app_2OIhiTGXt0Rh2WXy9Le8kD1mdPp/instances/ins_2OLiK9VnjvFtkdoQBxkTrfIbgNI/organizations/${organisation.id}`}
                target="_blank"
              >
                <div className="flex flex-row items-center gap-2 underline">
                  Clerk Org
                  <RiExternalLinkLine />
                </div>
              </a>
            </div>
          </Card>
        </div>

        <Card className="p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setBusinessEntitiesCollapsed((prev) => !prev)}
            aria-label="Toggle business entities"
          >
            <div className="flex items-center gap-2">
              <PiBuildingOffice />
              <Txt variant="card-title">Business Entities</Txt>
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                businessEntitiesCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>

          {!businessEntitiesCollapsed && (
            <>
              <div className="mt-4 flex w-full flex-row flex-wrap gap-4">
                {(data?.organisation?.businessEntities ?? []).map((be) => (
                  <Link
                    key={be.id}
                    to={`/organisations/${organisation.id}/business-entities/${be.id}`}
                  >
                    <Button variant="outline" className="gap-2">
                      <CurrencyFlagEmoji
                        className="rounded-xs"
                        currency={be.currency ?? organisation.homeCurrency}
                      />
                      <div>{be.name}</div>
                    </Button>
                  </Link>
                ))}
              </div>
              <div className="flex w-full flex-row justify-end">
                <NewBusinessEntityDialogTrigger
                  organisationName={data.organisation?.name ?? ""}
                  organisationId={data.organisation?.id ?? ""}
                  organisationActivityStatementFormat={
                    data.organisation?.activityStatementFormat ?? undefined
                  }
                  onCreated={(newId) =>
                    void navigate({
                      to: "/organisations/$organisationId/business-entities/$businessEntityId",
                      params: {
                        organisationId: data.organisation?.id ?? "",
                        businessEntityId: newId,
                      },
                    })
                  }
                >
                  <Button
                    size="sm"
                    variant="ghost"
                    className="justify-start text-start"
                  >
                    <PiPlus className="me-2 size-[1em]" />
                    Add new business entity
                  </Button>
                </NewBusinessEntityDialogTrigger>
              </div>
            </>
          )}
        </Card>

        <Card className="flex-1 p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setEditDetailsCollapsed((prev) => !prev)}
            aria-label="Toggle edit details"
          >
            <div className="flex items-center gap-2">
              <PiNotePencil />
              <Txt variant="card-title">Edit details</Txt>
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                editDetailsCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>
          {!editDetailsCollapsed && (
            <form.AppForm>
              <form
                className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void form.handleSubmit();
                }}
              >
                <div className="md:col-span-1">
                  <form.AppField name="homeCurrency">
                    {(field) => (
                      <field.SelectField label="Home currency">
                        {currencyValues.map((currency) => (
                          <field.SelectItem key={currency} value={currency}>
                            <span className="flex flex-row items-center gap-1">
                              <CurrencyFlagEmoji currency={currency} />
                              <Txt variant="body">{currency}</Txt>
                            </span>
                          </field.SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="userMfaRequirement">
                    {(field) => (
                      <field.SelectField label="User MFA requirement">
                        {userMfaRequirementValues.map((v) => (
                          <field.SelectItem key={v} value={v}>
                            {(() => {
                              switch (v) {
                                case "NOT_REQUIRED":
                                  return "Not required";
                                case "REQUIRED":
                                  return "Required";
                                default:
                                  return neverFunc(v);
                              }
                            })()}
                          </field.SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="longLogo">
                    {(field) => (
                      <ErrorTooltip
                        error={field.state.meta.errors.at(0)?.message}
                      >
                        <div className="flex flex-col gap-1">
                          <Label>Long logo</Label>
                          <Txt variant="caption" tone="secondary">
                            Recommended: in height 48px; no padding; transparent
                            bg.
                          </Txt>
                          <div className="mt-1 flex flex-row items-center justify-between gap-2">
                            {previewUrl ? (
                              <img
                                src={previewUrl}
                                className="h-12 rounded border"
                              />
                            ) : (
                              <div className="h-12 w-32 rounded border bg-neutral-muted" />
                            )}
                            <div className="flex flex-row gap-4">
                              <input
                                ref={longLogoRef}
                                className="hidden"
                                type="file"
                                accept="image/png"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) {
                                    return;
                                  }

                                  field.handleChange(file);
                                  setPreviewUrl(URL.createObjectURL(file));
                                }}
                              />
                              <Button
                                size="sm"
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  longLogoRef.current?.click();
                                }}
                              >
                                {previewUrl ? "Replace" : "Add"}
                              </Button>
                              {previewUrl && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  type="button"
                                  className="text-danger"
                                  onClick={(): void => {
                                    field.handleChange(null);
                                    setPreviewUrl(null);
                                  }}
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </ErrorTooltip>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <div className="pb-2">
                    <Txt variant="detail-label">Fixed monthly fee</Txt>
                  </div>
                  <div className="flex flex-row gap-2">
                    <div className="h-7">
                      <form.AppField name="fixedMonthlyFeeCurrency">
                        {(field) => (
                          <field.SelectField placeholder={"AUD"}>
                            {currencyValues.map((currency) => (
                              <field.SelectItem key={currency} value={currency}>
                                {currency}
                              </field.SelectItem>
                            ))}
                          </field.SelectField>
                        )}
                      </form.AppField>
                    </div>
                    <div className="flex-grow">
                      <form.Subscribe
                        selector={(state) =>
                          state.values.fixedMonthlyFeeCurrency
                        }
                      >
                        {(currency) => (
                          <form.AppField name="fixedMonthlyFeeAmount">
                            {(field) => (
                              <field.CurrencyAmountField
                                align="start"
                                size="lg"
                                disabled={!currency}
                                min={0}
                                currency={currency || organisation.homeCurrency}
                              />
                            )}
                          </form.AppField>
                        )}
                      </form.Subscribe>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-1">
                  <form.AppField name="fixedMonthlyFeeContractEndDate">
                    {(field) => (
                      <field.DatePickerField label="Fixed monthly fee contract end date" />
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="feePaymentMethod">
                    {(field) => (
                      <field.SelectField label="Fee payment method">
                        {organisationFeePaymentMethodValues.map((v) => (
                          <field.SelectItem key={v} value={v}>
                            {(() => {
                              switch (v) {
                                case "INVOICE":
                                  return "Invoice";
                                case "DIRECT_DEBIT":
                                  return "Direct debit from Macquarie CMA";
                                default:
                                  return neverFunc(v);
                              }
                            })()}
                          </field.SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="fxMarginRateBps">
                    {(field) => (
                      <ConnectedAnnotatedField label="FX trade fee margin rate bps">
                        <NumericFormatInput
                          className="min-h-10"
                          thousandSeparator=","
                          decimalScale={0}
                          allowNegative={false}
                          onBlur={field.handleBlur}
                          value={field.state.value}
                          onValueChange={field.handleChange}
                          endAdornment={
                            <div className="flex items-center">bps</div>
                          }
                        />
                      </ConnectedAnnotatedField>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-1">
                  <form.AppField name="activityStatementFormat">
                    {(field) => (
                      <field.SelectField label="Activity statement format">
                        <field.SelectItem value="XERO">Xero</field.SelectItem>
                        <field.SelectItem value="NETSUITE">
                          NetSuite
                        </field.SelectItem>
                      </field.SelectField>
                    )}
                  </form.AppField>
                </div>

                <div className="md:col-span-2">
                  <form.AppField name="xeroContactId">
                    {(field) => (
                      <field.InputField
                        type="text"
                        placeholder="5cf3a0ee-6c7a-42a9-8a2e-c70428e68922"
                        label=<span className="flex flex-row gap-1">
                          Xero Contact ID
                          <Tooltip>
                            <TooltipTrigger type="button">
                              <FaInfoCircle />
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="flex max-w-96 flex-col gap-2">
                                <Txt variant="caption" tone="inherit">
                                  Used for invoicing.
                                </Txt>
                                <Txt variant="caption" tone="inherit">
                                  Can be found by going to{" "}
                                  <a
                                    href="https://go.xero.com/app/!Fy3HT/contacts/all"
                                    target="_blank"
                                  >
                                    Contacts
                                  </a>
                                  , selecting the contact, then copying the ID
                                  from the URL.
                                </Txt>
                                <Txt variant="caption" tone="inherit">
                                  Will be a string like:{" "}
                                  <span className="text-nowrap font-mono">
                                    cf3a0ee-6c7a-42a9-8a2e-c70428e68922
                                  </span>{" "}
                                  after{" "}
                                  <span className="font-mono">/contact</span>)
                                </Txt>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      />
                    )}
                  </form.AppField>
                </div>

                <div className="mt-4 flex w-full flex-row justify-end md:col-span-3">
                  <form.Subscribe selector={(state) => state.isDirty}>
                    {(isDirty) => (
                      <form.SubmitButton
                        disabled={!isDirty || loading}
                        childrenWhileSubmitting="Saving..."
                      >
                        Save
                      </form.SubmitButton>
                    )}
                  </form.Subscribe>
                </div>
              </form>
            </form.AppForm>
          )}
        </Card>

        <Card className="p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setApprovalsCollapsed((prev) => !prev)}
            aria-label="Toggle approval policies"
          >
            <div className="flex items-center gap-2">
              <PiCheckCircle />
              <Txt variant="card-title">Approval policies</Txt>
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                approvalsCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>
          {!approvalsCollapsed && (
            <div className="flex flex-row gap-4 pt-4">
              {policyDefinitions.map((definition) => (
                <ApprovalPolicyCard
                  organisationId={organisation.id}
                  key={definition.kind}
                  definition={definition}
                />
              ))}
            </div>
          )}
        </Card>

        <Card className="p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setUsersCollapsed((prev) => !prev)}
            aria-label="Toggle users"
          >
            <div className="flex items-center gap-2">
              <PiUser />
              <Txt variant="card-title">
                Users ({organisation.memberships?.length ?? 0})
              </Txt>
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                usersCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>
          {!usersCollapsed && (
            <div className="mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">User</th>
                    <th className="pb-2 font-medium">Notifications</th>
                  </tr>
                </thead>
                <tbody>
                  {(organisation.memberships ?? [])
                    .filter(
                      (m) =>
                        m.userProfile !== null && m.userProfile !== undefined,
                    )
                    .map((membership) => {
                      const userProfile = membership.userProfile;
                      if (!userProfile) {
                        return null;
                      }
                      return (
                        <tr
                          key={membership.id}
                          className="border-b last:border-0"
                        >
                          <td className="py-2 pr-4">
                            <UserProfile {...userProfile} />
                          </td>
                          <td className="py-2">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={
                                  userProfile.notificationsEnabled ?? true
                                }
                                disabled={notificationMutationResult.loading}
                                onCheckedChange={async (checked) => {
                                  notificationLoadingUserIdRef.current =
                                    userProfile.id;
                                  try {
                                    await setUserNotificationsEnabled({
                                      variables: {
                                        input: {
                                          userProfileId: userProfile.id,
                                          notificationsEnabled: checked,
                                        },
                                      },
                                    });
                                  } catch {
                                    toast({
                                      kind: "error",
                                      autoHide: false,
                                      title:
                                        "Failed to update notification preference",
                                    });
                                  }
                                }}
                                aria-label={`Toggle notifications for ${userProfile.firstName ?? userProfile.email}`}
                              />
                              {notificationLoadingUserIdRef.current ===
                                userProfile.id && (
                                <InlineSaveStatus
                                  mutationResult={notificationMutationResult}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card className="p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setRulesCollapsed((prev) => !prev)}
            aria-label="Toggle rules"
          >
            <div className="flex items-center gap-2">
              <PiRobot />
              <Txt variant="card-title">
                Rules (
                {organisation.assetRulesSummary?.activeAssetRules?.filter(
                  (r) => r.status === "ACTIVE",
                )?.length ?? 0}
                )
              </Txt>
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                rulesCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>
          {!rulesCollapsed && (
            <div className="mt-4 flex flex-col gap-4">
              <DataTable
                columns={assetRuleColumns}
                data={organisation.assetRulesSummary?.activeAssetRules ?? []}
                hidePagination
                showCsvExport={false}
                onRowClick={(row) => {
                  void navigate({
                    to: "/asset-rules/$ruleId",
                    params: { ruleId: row.original.id },
                  });
                }}
                emptyState={
                  <Txt variant="description">No rules configured</Txt>
                }
              />
              <div className="flex flex-col items-end">
                <Link
                  to="/organisations/$organisationId/rules"
                  params={{ organisationId }}
                >
                  <Button variant="outline">Rules visualisation</Button>
                </Link>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-8">
          <Button
            variant="ghost"
            className="flex h-auto w-full flex-row items-center justify-between gap-2 rounded-none p-0 font-normal"
            onClick={() => setExternalConnectionsCollapsed((prev) => !prev)}
            aria-label="Toggle external connections"
          >
            <div className="flex items-center gap-2">
              <PiPlugsConnected />
              <Txt variant="card-title">External Connections</Txt>
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                externalConnectionsCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </Button>
          {!externalConnectionsCollapsed && (
            <div className="mt-4 flex flex-col gap-4">
              <ExternalConnectionsCard data={organisation} />
              <div className="flex justify-end">
                <AddManualAccountDialogTrigger
                  organisationId={organisation.id}
                  businessEntities={
                    data?.organisation?.businessEntities?.map((be) => ({
                      id: be.id,
                      name: be.name,
                    })) ?? []
                  }
                  defaultCurrency={organisation.homeCurrency}
                >
                  <Button variant="outline" className="gap-2">
                    <PiPlus />
                    Add manually tracked account
                  </Button>
                </AddManualAccountDialogTrigger>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-8">
          <button
            className="flex w-full flex-row items-center justify-between gap-2 text-left"
            onClick={() => setNotificationsCollapsed((prev) => !prev)}
            aria-label="Toggle notifications"
          >
            <div className="flex items-center gap-2">
              <PiMailbox />
              <Txt variant="card-title">Notifications</Txt>
            </div>
            <div
              className={cn(
                "transition-transform duration-300",
                notificationsCollapsed ? "rotate-0" : "rotate-180",
              )}
            >
              <RiArrowDownSLine className="size-5" />
            </div>
          </button>
          {!notificationsCollapsed && (
            <div className="mt-4">
              <DataTable
                columns={[
                  {
                    id: "date",
                    header: "Date",
                    cell: ({ row }) => (
                      <DateUI
                        date={row.original.createdAt}
                        variant="DATETIME_TZ"
                      />
                    ),
                  },
                  {
                    id: "kind",
                    header: "Kind",
                    cell: ({ row }) => (
                      <Txt variant="mono">{row.original.kind}</Txt>
                    ),
                  },
                  {
                    id: "status",
                    header: "Status",
                    cell: ({ row }) =>
                      prettyPrintEnum(row.original.recoveryPoint),
                  },
                ]}
                data={notifications}
                isLoadingData={
                  notificationsLoading ||
                  notificationsNetworkStatus === NetworkStatus.fetchMore
                }
                hidePagination
                showCsvExport={false}
                onRowClick={(row) => {
                  void navigate({
                    to: "/notifications/$notificationId",
                    params: { notificationId: row.original.id },
                  });
                }}
                emptyState={
                  <Txt variant="description">No notifications sent</Txt>
                }
              />
              <div className="mt-4 flex w-full flex-row justify-center">
                {notificationsNextCursor ? (
                  <Button
                    onClick={() =>
                      fetchMoreNotifications({
                        variables: {
                          after: notificationsNextCursor,
                          id: organisationId,
                          first: NOTIFICATIONS_PAGE_SIZE,
                        },
                      })
                    }
                    variant="outline"
                    loading={
                      notificationsLoading ||
                      notificationsNetworkStatus === NetworkStatus.fetchMore
                    }
                  >
                    Load more
                  </Button>
                ) : (
                  !!notifications.length && (
                    <p className="text-secondary text-sm">No more data</p>
                  )
                )}
              </div>
            </div>
          )}
        </Card>

        <div className="relative">
          <LoadingToastProvider animationUrl={loadingAnimation}>
            <AccountList
              collapseSidebar
              filterStateKey={`ops_accounts_${organisationId}`}
              organisationId={organisationId}
              title={`Total value of accounts (${organisation.homeCurrency})`}
              headlineChart="balanceTimeSeries"
              assetLinkPrefixOverride="/assets"
              beneficiaryLinkPrefixOverride="/beneficiary-accounts"
              columns={["ACCOUNT", "BUSINESS_ENTITY", "IDENTIFIER", "BALANCE"]}
              alwaysShowBusinessEntityColumn
              TreasuryIcon={RiBankLine}
            />
          </LoadingToastProvider>
        </div>

        <Card className="border border-danger bg-danger-muted/40 p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-2">
              <Txt variant="card-title" tone="danger">
                Danger zone
              </Txt>
              <Txt variant="body" tone="danger">
                Deactivating an organisation prevents further activity and
                access in the admin portal.
              </Txt>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="destructive">Deactivate organisation</Button>
              </DialogTrigger>
              <DialogContent>
                <MfaFlow
                  promptVariant="dialog-content"
                  onResolve={handleDeactivateOrganisation}
                >
                  {(requestMfa) => (
                    <>
                      <DialogHeader>
                        <DialogTitle>Deactivate organisation</DialogTitle>
                        <DialogDescription>
                          This action disables the organisation and cannot be
                          undone from this page.
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        className="flex flex-col gap-6"
                        onSubmit={async (event) => {
                          event.preventDefault();
                          await requestMfa();
                        }}
                      >
                        <DialogFooter className="flex items-center gap-2">
                          <DialogClose asChild>
                            <Button type="button" variant="secondary">
                              Cancel
                            </Button>
                          </DialogClose>
                          <Button
                            type="submit"
                            variant="destructive"
                            disabled={deactivateLoading}
                          >
                            {deactivateLoading
                              ? "Deactivating..."
                              : "Deactivate organisation"}
                          </Button>
                        </DialogFooter>
                      </form>
                    </>
                  )}
                </MfaFlow>
              </DialogContent>
            </Dialog>
          </div>
        </Card>
      </div>
    </AdminPageContainer>
  );
}

const newBusinessEntityFormSchema = (requiresNetsuiteCustomerId: boolean) =>
  z
    .object({
      name: z.string().min(1, "Please enter a name"),
      netsuiteInternalCustomerId: z.string().optional(),
    })
    .superRefine((value, ctx) => {
      if (
        requiresNetsuiteCustomerId &&
        !value.netsuiteInternalCustomerId?.trim()
      ) {
        ctx.addIssue({
          path: ["netsuiteInternalCustomerId"],
          code: "custom",
          message:
            "This organisation is configured to use NetSuite activity statements. A NetSuite internal customer ID is required for all business entities.",
        });
      }
    });
function NewBusinessEntityDialogTrigger({
  children,
  organisationId,
  organisationName,
  organisationActivityStatementFormat,
  onCreated,
}: {
  children: ReactNode;
  onCreated?: (newId: string) => void;
  organisationId: string;
  organisationName: string;
  organisationActivityStatementFormat?: ActivityStatementFormat;
}) {
  const [createBusinessEntity] = useMutation(
    graphql(`
      mutation CreateBusinessEntity($name: String!, $organisationId: ID!, $netsuiteInternalCustomerId: ID) {
        createBusinessEntity(
          input: {name: $name, organisationId: $organisationId, netsuiteInternalCustomerId: $netsuiteInternalCustomerId}
        ) {
          businessEntity {
            id
            name
          }
        }
      }
    `),
  );
  const [open, setOpen] = useState(false);
  const requiresNetsuiteCustomerId =
    organisationActivityStatementFormat === "NETSUITE";
  const form = useAppForm({
    onSubmit: async ({ value }) => {
      const result = await createBusinessEntity({
        variables: {
          name: value.name,
          organisationId,
          netsuiteInternalCustomerId: requiresNetsuiteCustomerId
            ? value.netsuiteInternalCustomerId?.trim()
            : undefined,
        },
      });

      if (!result.data) {
        throw Error("Failed to create new business entity");
      }
      setOpen(false);
      onCreated?.(result.data.createBusinessEntity.businessEntity!.id);
    },
    validators: {
      onSubmit: newBusinessEntityFormSchema(requiresNetsuiteCustomerId),
    },
    defaultValues: {
      name: "",
      netsuiteInternalCustomerId: "",
    } as z.input<ReturnType<typeof newBusinessEntityFormSchema>>,
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent asChild>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="grid gap-6"
        >
          <form.AppForm>
            <DialogHeader>
              <BreadcrumbArray
                stack={[
                  {
                    title: organisationName,
                  },
                ]}
              />
              <DialogTitle>New business entity</DialogTitle>
              <DialogDescription className="sr-only">
                Use this form to create a new business entity
              </DialogDescription>
            </DialogHeader>
            <form.AppField name="name">
              {(field) => (
                <field.InputField
                  label="Name"
                  placeholder="Enter a name for the new business entity"
                />
              )}
            </form.AppField>
            {requiresNetsuiteCustomerId && (
              <form.AppField name="netsuiteInternalCustomerId">
                {(field) => (
                  <field.InputField
                    label="NetSuite internal customer ID"
                    placeholder={
                      requiresNetsuiteCustomerId
                        ? "Required for this organisation"
                        : "Optional for this organisation"
                    }
                  />
                )}
              </form.AppField>
            )}
            <DialogFooter>
              <form.SubmitButton childrenWhileSubmitting="Creating business entity...">
                Create business entity
              </form.SubmitButton>
            </DialogFooter>
          </form.AppForm>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ApprovalPolicyCard = ({
  definition,
  organisationId,
}: {
  definition: ApprovalPolicy;
  organisationId: string;
}) => {
  const [setOrganisationApprovalPolicies, { loading }] = useMutation(
    graphql(`
      mutation SetOrganisationApprovalPoliciesForAdmin($input: SetOrganisationApprovalPoliciesInput!) {
        setOrganisationApprovalPolicies(input: $input) {
          approvalPolicies {
            id
            allowPolicyOverride
            allowSelfReview
            numUserReviewsRequired
          }
        }
      }
    `),
  );

  const form = useAppForm({
    defaultValues: {
      kind: definition.kind,
      numUserReviewsRequired: String(
        definition.approvalPolicy?.numUserReviewsRequired ?? 0,
      ) as ApprovalPolicyFormValues["numUserReviewsRequired"],
      allowSelfReview: definition.approvalPolicy?.allowSelfReview ?? false,
      allowPolicyOverride:
        definition.approvalPolicy?.allowPolicyOverride ?? false,
    },
    validators: {
      onSubmit: approvalPolicyFormSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = approvalPolicyFormSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Please fix the validation errors",
        });
        return;
      }

      await setOrganisationApprovalPolicies({
        variables: {
          input: {
            organisationId,
            approvalPolicies: [
              {
                kind: parsed.data.kind,
                numUserReviewsRequired: Number(
                  parsed.data.numUserReviewsRequired,
                ),
                allowPolicyOverride: !!parsed.data.allowPolicyOverride,
                allowSelfReview: !!parsed.data.allowSelfReview,
              },
            ],
          },
        },
        onCompleted: () => {
          toast({
            kind: "success",
            autoHide: true,
            title: "Approval policy saved",
          });
        },
        onError: (error) => {
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to save approval policy",
            description:
              error.graphQLErrors?.[0]?.message ?? error.message ?? undefined,
          });
        },
      });
    },
  });

  const approvalPolicyExists = !!definition.approvalPolicy;
  return (
    <div className="flex-[1] overflow-hidden">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-4"
      >
        <form.AppForm>
          <Txt variant="section-title">{prettyPrintEnum(definition.kind)}</Txt>
          <div className="flex flex-col gap-6">
            {!approvalPolicyExists && (
              <Alert
                kind="warning"
                title="No approval policy"
                description={`This organisation does not have an approval policy. ${prettyPrintEnum(definition.kind)} functionality will not work until this is resolved. Create an approval policy using the form below.`}
              />
            )}

            <form.AppField name="numUserReviewsRequired">
              {(field) => (
                <field.SelectField label="Number of user reviews required">
                  {["0", "1", "2", "3", "4"].map((value) => (
                    <field.SelectItem key={value} value={value}>
                      {value}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="allowSelfReview">
              {(field) => (
                <field.SwitchField
                  label="Allow admins to self-review"
                  description={
                    <p className="text-secondary text-xs">{`When enabled, approvable ${prettyPrintEnum(
                      definition.kind,
                    )} created by organisation admins will automatically receive an approval from the user who created them.`}</p>
                  }
                />
              )}
            </form.AppField>

            <form.AppField name="allowPolicyOverride">
              {(field) => (
                <field.SwitchField
                  label="Allow admins to override review requirements"
                  description={
                    <p className="text-secondary text-xs">{`When enabled, approvable ${prettyPrintEnum(definition.kind)} created by organisation admins will be considered fully approved, regardless of how many approvals are required by this policy.`}</p>
                  }
                />
              )}
            </form.AppField>

            <div className="flex">
              <form.SubmitButton
                size="sm"
                disabled={loading}
                childrenWhileSubmitting={
                  approvalPolicyExists ? "Saving..." : "Creating..."
                }
              >
                {approvalPolicyExists ? "Save changes" : "Create policy"}
              </form.SubmitButton>
            </div>
          </div>
        </form.AppForm>
      </form>
    </div>
  );
};

type OrgConn = NonNullable<AdminOrganisationQuery["organisation"]>;

const ALL_PROVIDERS = [
  "Airwallex",
  "Akahu",
  "Brex",
  "CurrencyCloud",
  "Ebury",
  "Mercury",
  "Quiltt",
  "Ramp",
  "SaltEdge",
  "Skript",
  "Stripe",
  "Xero",
];

type ConnAsset = {
  id: string;
  externalId?: string;
  name: string;
  estimatedValue?: { amount: Decimal; currency: string } | null;
  createdAt: Date;
};

type ConnItem = {
  id: string;
  idLabel: string;
  label: string;
  detail?: string;
  status?: string;
  businessEntityName?: string;
  assets: ConnAsset[];
  revokeClientId?: string;
};

type ProviderSection = {
  name: string;
  items: ConnItem[];
};

function mergeInto(
  sections: ProviderSection[],
  provider: string,
  items: ConnItem[],
) {
  let section = sections.find((s) => s.name === provider);
  if (!section) {
    section = { name: provider, items: [] };
    sections.push(section);
  }
  section.items.push(...items);
}

function buildSections(data: OrgConn): ProviderSection[] {
  const sections: ProviderSection[] = [];

  if (data.xeroConnection) {
    sections.push({
      name: "Xero",
      items: [
        {
          id: data.xeroConnection.id,
          idLabel: "connection",
          label: prettyPrintEnum(data.xeroConnection.status),
          detail: data.xeroConnection.lastSyncedAt
            ? `Last synced ${data.xeroConnection.lastSyncedAt.toLocaleDateString()}`
            : undefined,
          status: data.xeroConnection.status,
          assets: [],
        },
      ],
    });
  }

  for (const be of data.businessEntities ?? []) {
    const awxAssets = new Map<string, ConnAsset[]>();
    const seAssets = new Map<string, ConnAsset[]>();
    const akahuAssets: ConnAsset[] = [];
    const quilttConns = new Map<
      string,
      { label: string; assets: ConnAsset[] }
    >();
    const stripeConns = new Map<
      string,
      { label: string; detail?: string; status?: string; assets: ConnAsset[] }
    >();
    const mercuryConns = new Map<
      string,
      { label: string; assets: ConnAsset[] }
    >();
    const brexConns = new Map<string, { assets: ConnAsset[] }>();
    const rampConns = new Map<string, { label: string; assets: ConnAsset[] }>();

    for (const acc of be.externalAccounts ?? []) {
      if (acc.__typename === "AirwallexExternalAccountDetails") {
        const cid = acc.awxConn?.id;
        if (cid && acc.asset) {
          const list = awxAssets.get(cid) ?? [];
          list.push({ ...(acc.asset as ConnAsset), externalId: acc.id });
          awxAssets.set(cid, list);
        }
      } else if (acc.__typename === "AkahuExternalAccountDetails") {
        if (acc.asset) {
          akahuAssets.push(acc.asset as ConnAsset);
        }
      } else if (acc.__typename === "SaltEdgeExternalAccountDetails") {
        const cid = acc.seConn?.id;
        if (cid && acc.asset) {
          const list = seAssets.get(cid) ?? [];
          list.push(acc.asset as ConnAsset);
          seAssets.set(cid, list);
        }
      } else if (acc.__typename === "QuilttExternalAccountDetails") {
        const cid = acc.qConn?.id;
        if (cid) {
          const existing = quilttConns.get(cid);
          const asset = acc.asset ? (acc.asset as ConnAsset) : null;
          if (existing) {
            if (asset) {
              existing.assets.push(asset);
            }
          } else {
            quilttConns.set(cid, {
              label: acc.providerFriendlyName ?? acc.name,
              assets: asset ? [asset] : [],
            });
          }
        }
      } else if (acc.__typename === "StripeExternalAccountDetails") {
        const cid = acc.stConn?.id;
        if (cid) {
          const existing = stripeConns.get(cid);
          const asset = acc.asset ? (acc.asset as ConnAsset) : null;
          if (existing) {
            if (asset) {
              existing.assets.push(asset);
            }
          } else {
            stripeConns.set(cid, {
              label: "Connected",
              detail: acc.stConn?.stripeAccountId ?? undefined,
              status: acc.stConn?.status ?? undefined,
              assets: asset ? [asset] : [],
            });
          }
        }
      } else if (acc.__typename === "MercuryExternalAccountDetails") {
        const cid = acc.mConn?.id;
        if (cid) {
          const existing = mercuryConns.get(cid);
          const asset = acc.asset ? (acc.asset as ConnAsset) : null;
          if (existing) {
            if (asset) {
              existing.assets.push(asset);
            }
          } else {
            mercuryConns.set(cid, {
              label: acc.name,
              assets: asset ? [asset] : [],
            });
          }
        }
      } else if (acc.__typename === "BrexExternalAccountDetails") {
        const cid = acc.bConn?.id;
        if (cid) {
          const existing = brexConns.get(cid);
          const asset = acc.asset ? (acc.asset as ConnAsset) : null;
          if (existing) {
            if (asset) {
              existing.assets.push(asset);
            }
          } else {
            brexConns.set(cid, { assets: asset ? [asset] : [] });
          }
        }
      } else if (acc.__typename === "RampExternalAccountDetails") {
        const cid = acc.rConn?.id;
        if (cid) {
          const existing = rampConns.get(cid);
          const asset = acc.asset ? (acc.asset as ConnAsset) : null;
          if (existing) {
            if (asset) {
              existing.assets.push(asset);
            }
          } else {
            rampConns.set(cid, {
              label: acc.name,
              assets: asset ? [asset] : [],
            });
          }
        }
      }
    }

    if ((be.activeAirwallexConnections ?? []).length > 0) {
      mergeInto(
        sections,
        "Airwallex",
        be.activeAirwallexConnections!.map((conn) => ({
          id: conn.id,
          idLabel: "connection",
          label: conn.accountName ?? "Connected",
          detail: `clientId: ${conn.clientId}`,
          status: conn.status,
          businessEntityName: be.name,
          assets: awxAssets.get(conn.id) ?? [],
          revokeClientId: conn.clientId,
        })),
      );
    }

    if (be.activeAkahuConnection) {
      mergeInto(sections, "Akahu", [
        {
          id: be.activeAkahuConnection.id,
          idLabel: "connection",
          label: "Connected",
          businessEntityName: be.name,
          assets: akahuAssets,
        },
      ]);
    }

    if ((be.activeSaltEdgeConnections ?? []).length > 0) {
      mergeInto(
        sections,
        "SaltEdge",
        be.activeSaltEdgeConnections!.map((conn) => ({
          id: conn.id,
          idLabel: "connection",
          label: conn.providerName,
          businessEntityName: be.name,
          assets: seAssets.get(conn.id) ?? [],
        })),
      );
    }

    if (be.skriptConnection) {
      const arrangements = be.skriptConnection.arrangements ?? [];
      const items: ConnItem[] =
        arrangements.length > 0
          ? arrangements.map((arr) => ({
              id: arr.id,
              idLabel: "arrangement",
              label: prettyPrintEnum(arr.status),
              status: arr.status,
              businessEntityName: be.name,
              assets: (arr.accounts ?? [])
                .filter((a) => a.asset)
                .map((a) => a.asset as ConnAsset),
            }))
          : [
              {
                id: be.skriptConnection.id,
                idLabel: "connection",
                label: "Connected",
                detail: `skriptId: ${be.skriptConnection.skriptId}`,
                businessEntityName: be.name,
                assets: [],
              },
            ];
      mergeInto(sections, "Skript", items);
    }

    if (be.currencycloudAccount) {
      mergeInto(sections, "CurrencyCloud", [
        {
          id: be.currencycloudAccount.id,
          idLabel: "account",
          label: be.currencycloudAccount.accountName,
          status: be.currencycloudAccount.status,
          businessEntityName: be.name,
          assets: [],
        },
      ]);
    }

    if (be.eburyClient) {
      mergeInto(sections, "Ebury", [
        {
          id: be.eburyClient.id,
          idLabel: "client",
          label: be.eburyClient.clientName ?? "-",
          businessEntityName: be.name,
          assets: [],
        },
      ]);
    }

    if (quilttConns.size > 0) {
      mergeInto(
        sections,
        "Quiltt",
        Array.from(quilttConns.entries()).map(([id, c]) => ({
          id,
          idLabel: "connection",
          label: c.label,
          businessEntityName: be.name,
          assets: c.assets,
        })),
      );
    }

    if (stripeConns.size > 0) {
      mergeInto(
        sections,
        "Stripe",
        Array.from(stripeConns.entries()).map(([id, c]) => ({
          id,
          idLabel: "connection",
          label: c.label,
          detail: c.detail,
          status: c.status,
          businessEntityName: be.name,
          assets: c.assets,
        })),
      );
    }

    if (mercuryConns.size > 0) {
      mergeInto(
        sections,
        "Mercury",
        Array.from(mercuryConns.entries()).map(([id, c]) => ({
          id,
          idLabel: "connection",
          label: c.label,
          businessEntityName: be.name,
          assets: c.assets,
        })),
      );
    }

    if (brexConns.size > 0) {
      mergeInto(
        sections,
        "Brex",
        Array.from(brexConns.entries()).map(([id, c]) => ({
          id,
          idLabel: "connection",
          label: "Connected",
          businessEntityName: be.name,
          assets: c.assets,
        })),
      );
    }

    if (rampConns.size > 0) {
      mergeInto(
        sections,
        "Ramp",
        Array.from(rampConns.entries()).map(([id, c]) => ({
          id,
          idLabel: "connection",
          label: c.label,
          businessEntityName: be.name,
          assets: c.assets,
        })),
      );
    }
  }

  return sections;
}

function statusColor(status: string | undefined) {
  if (!status) {
    return "";
  }
  if (status === "ACTIVE" || status === "ENABLED") {
    return "bg-success-muted text-success";
  }
  if (status === "RECONNECT_REQUIRED" || status === "DISABLED") {
    return "bg-danger-muted text-danger";
  }
  return "bg-warning-muted text-warning";
}

function StatusBadge({ status }: { status: string }) {
  return (
    <div className={cn("rounded-full px-2 py-0.5", statusColor(status))}>
      <Txt variant="caption" tone="inherit">
        {prettyPrintEnum(status)}
      </Txt>
    </div>
  );
}

function ConnItemAssets({
  assets,
  businessEntityName,
  isMultiEntity,
}: {
  assets: ConnAsset[];
  businessEntityName?: string;
  isMultiEntity: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  if (assets.length === 0) {
    return null;
  }

  const hasExternalIds = assets.some((a) => a.externalId);

  const columns: ColumnDef<ConnAsset>[] = [
    {
      id: "name",
      header: "Account",
      cell: ({ row }) => <Txt variant="caption">{row.original.name}</Txt>,
    },
    ...(isMultiEntity && businessEntityName
      ? [
          {
            id: "businessEntity",
            header: "Entity",
            cell: () => (
              <Txt variant="caption" tone="secondary">
                {businessEntityName}
              </Txt>
            ),
          } satisfies ColumnDef<ConnAsset>,
        ]
      : []),
    {
      id: "balance",
      header: "Balance",
      cell: ({ row }) => (
        <CurrencyAmountUI
          amount={row.original.estimatedValue?.amount}
          currency={row.original.estimatedValue?.currency}
        />
      ),
    },
    {
      id: "createdAt",
      header: "Created",
      cell: ({ row }) => <DateUI date={row.original.createdAt} />,
    },
    {
      id: "assetId",
      header: "Asset ID",
      cell: ({ row }) => <Txt variant="mono">{row.original.id}</Txt>,
    },
    ...(hasExternalIds
      ? [
          {
            id: "externalId",
            header: "Row ID",
            cell: ({ row }) =>
              row.original.externalId ? (
                <Txt variant="mono">{row.original.externalId}</Txt>
              ) : (
                <span>—</span>
              ),
          } satisfies ColumnDef<ConnAsset>,
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="flex items-center gap-1 text-left"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div
          className={cn(
            "transition-transform duration-200",
            expanded ? "rotate-180" : "rotate-0",
          )}
        >
          <RiArrowDownSLine className="size-3.5 text-secondary" />
        </div>
        <Txt variant="caption" tone="secondary">
          {assets.length} {assets.length === 1 ? "account" : "accounts"}
        </Txt>
      </button>
      {expanded && (
        <div className="overflow-x-auto">
          <DataTable data={assets} columns={columns} />
        </div>
      )}
    </div>
  );
}

function ConnItemRow({
  item,
  isMultiEntity,
}: {
  item: ConnItem;
  isMultiEntity: boolean;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [revoke, { loading: revoking }] = useMutation(
    graphql(`
      mutation RevokeAirwallexConnectionById($input: RevokeAirwallexConnectionByIdInput!) {
        revokeAirwallexConnectionById(input: $input) {
          organisation {
            id
          }
        }
      }
    `),
    {
      refetchQueries: ["AdminOrganisation"],
      onCompleted: () => {
        setConfirmOpen(false);
        toast({ kind: "success", title: "Connection revoked", autoHide: true });
      },
      onError: () => {
        toast({
          kind: "error",
          title: "Failed to revoke connection",
          autoHide: false,
        });
      },
    },
  );

  const showRevoke =
    item.revokeClientId != null && item.status === "RECONNECT_REQUIRED";

  return (
    <div className="flex flex-col gap-2 rounded-md border border-grid p-3">
      <div className="flex flex-row items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <div className="flex flex-row flex-wrap items-center gap-2">
            <Txt variant="field-label">{item.label}</Txt>
            {item.businessEntityName && (
              <Txt variant="caption" tone="secondary">
                {item.businessEntityName}
              </Txt>
            )}
            {item.status && <StatusBadge status={item.status} />}
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            <Txt variant="mono">
              {item.idLabel}: {item.id}
            </Txt>
            {item.detail && (
              <Txt variant="mono" tone="secondary">
                {item.detail}
              </Txt>
            )}
          </div>
        </div>

        {showRevoke && (
          <>
            <Button
              variant="ghost"
              size="sm"
              icon={<PiX />}
              aria-label="Revoke"
              className="shrink-0 text-danger"
              onClick={() => setConfirmOpen(true)}
            />
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Revoke Airwallex connection?</DialogTitle>
                  <DialogDescription>
                    This will permanently revoke the connection for{" "}
                    <Txt variant="mono">{item.revokeClientId}</Txt>. This cannot
                    be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" disabled={revoking}>
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button
                    variant="destructive"
                    loading={revoking}
                    onClick={() => {
                      void revoke({
                        variables: {
                          input: { connectionId: item.id },
                        },
                      });
                    }}
                  >
                    Revoke connection
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>

      <ConnItemAssets
        assets={item.assets}
        businessEntityName={item.businessEntityName}
        isMultiEntity={isMultiEntity}
      />
    </div>
  );
}

function ExternalConnectionsCard({
  data,
}: {
  data: AdminOrganisationQuery["organisation"];
}) {
  if (!data) {
    return (
      <Txt variant="description" tone="secondary">
        Loading…
      </Txt>
    );
  }

  const sections = buildSections(data);
  const activeProviders = new Set(sections.map((s) => s.name));
  const inactiveProviders = ALL_PROVIDERS.filter(
    (p) => !activeProviders.has(p),
  );
  const isMultiEntity = (data.businessEntities?.length ?? 0) > 1;

  return (
    <div className="flex flex-col gap-6">
      {data.pendingExternalAccountLinks &&
        data.pendingExternalAccountLinks.length > 0 && (
          <div className="flex flex-row gap-4 rounded-lg border border-warning bg-primary p-4 ring-4 ring-warning">
            <PiArrowsClockwise size={24} className="shrink-0 text-warning" />
            <div className="flex flex-col gap-2">
              <Txt variant="field-label" tone="warning">
                Synchronising accounts
              </Txt>
              <Txt variant="body">
                Started synchronising from{" "}
                {data.pendingExternalAccountLinks
                  .map((pl) => pl.provider)
                  .join(", ")}
                . Accounts will be available shortly — usually within 15
                minutes.
              </Txt>
            </div>
          </div>
        )}

      {sections.length === 0 && (
        <Txt variant="description" tone="secondary">
          No external connections active for this organisation.
        </Txt>
      )}

      {sections.map((section) => (
        <div key={section.name} className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <PiPlugsConnected className="size-4 text-secondary" />
            <Txt variant="card-title">{section.name}</Txt>
          </div>

          <div className="flex flex-col gap-3">
            {section.items.map((item) => (
              <ConnItemRow
                key={item.id}
                item={item}
                isMultiEntity={isMultiEntity}
              />
            ))}
          </div>
        </div>
      ))}

      {inactiveProviders.length > 0 && (
        <div className="flex flex-col gap-2">
          <Txt variant="card-title" tone="secondary">
            Not active
          </Txt>
          <div className="flex flex-row flex-wrap gap-2">
            {inactiveProviders.map((provider) => (
              <div
                key={provider}
                className="flex flex-row items-center gap-1.5 rounded-md border border-grid bg-primary/60 px-3 py-1.5 opacity-60"
              >
                <PiPlug className="size-3.5 text-secondary" />
                <Txt variant="caption" tone="secondary">
                  {provider}
                </Txt>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const addManualAccountFormSchema = z.object({
  name: z.string().min(1, "Please enter an account name"),
  businessEntityId: z.string().min(1, "Please select a business entity"),
  currency: z.enum(currencyValues),
  initialBalance: decimalSchema,
});

function AddManualAccountDialogTrigger({
  children,
  organisationId,
  businessEntities,
  defaultCurrency,
}: {
  children: ReactNode;
  organisationId: string;
  businessEntities: { id: string; name: string }[];
  defaultCurrency: string;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [createManualEntryAccount] = useMutation(
    graphql(`
      mutation CreateManualEntryExternalAccount($organisationId: ID!, $businessEntityId: ID!, $name: String!, $currency: Currency!, $initialBalance: Decimal!) {
        createManualEntryExternalAccount(
          input: {organisationId: $organisationId, businessEntityId: $businessEntityId, name: $name, currency: $currency, initialBalance: $initialBalance}
        ) {
          asset {
            id
            name
          }
        }
      }
    `),
    { refetchQueries: ["AccountListOrgData"] },
  );

  const form = useAppForm({
    onSubmit: async ({ value }) => {
      try {
        const result = await createManualEntryAccount({
          variables: {
            organisationId,
            businessEntityId: value.businessEntityId,
            name: value.name,
            currency: value.currency,
            initialBalance: value.initialBalance,
          },
        });

        if (result.errors?.length) {
          throw new Error(result.errors[0]?.message ?? "Unknown error");
        }

        toast({
          kind: "success",
          title: `Created "${result.data?.createManualEntryExternalAccount.asset?.name}"`,
          autoHide: true,
        });
        setOpen(false);
        const assetId = result.data?.createManualEntryExternalAccount.asset?.id;
        if (assetId) {
          void navigate({ to: "/assets/$assetId", params: { assetId } });
        }
      } catch (e) {
        toast({
          kind: "error",
          title:
            e instanceof Error
              ? e.message
              : "Failed to create manually tracked account",
          autoHide: false,
        });
      }
    },
    validators: {
      onSubmit: addManualAccountFormSchema,
    },
    defaultValues: {
      name: "",
      businessEntityId: businessEntities[0]?.id ?? "",
      currency: isCurrency(defaultCurrency) ? defaultCurrency : "AUD",
      initialBalance: undefined as unknown as Decimal,
    } as z.input<typeof addManualAccountFormSchema>,
  });

  const selectedCurrency = useStore(
    form.store,
    (state) => state.values.currency,
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent asChild>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="grid gap-6"
        >
          <form.AppForm>
            <DialogHeader>
              <DialogTitle>Add manually tracked account</DialogTitle>
              <DialogDescription>
                Track a balance-only account that is updated manually.
              </DialogDescription>
            </DialogHeader>
            <form.AppField name="name">
              {(field) => (
                <field.InputField
                  label="Account name"
                  placeholder="e.g. Petty cash, Investment portfolio"
                />
              )}
            </form.AppField>
            <form.AppField name="businessEntityId">
              {(field) => (
                <field.SelectField label="Business entity">
                  {businessEntities.map((be) => (
                    <field.SelectItem key={be.id} value={be.id}>
                      {be.name}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>
            <form.AppField name="currency">
              {(field) => (
                <field.SelectField label="Currency">
                  {currencyValues.map((currency) => (
                    <field.SelectItem key={currency} value={currency}>
                      <span className="flex flex-row items-center gap-1">
                        <CurrencyFlagEmoji currency={currency} />
                        <Txt variant="body">{currency}</Txt>
                      </span>
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>
            <form.AppField name="initialBalance">
              {(field) => (
                <field.CurrencyAmountField
                  label="Current balance"
                  placeholder="0.00"
                  currency={selectedCurrency}
                  includeCurrencyPrefix={false}
                  selectableCurrency={false}
                  allowNegative={false}
                  decimalScale={2}
                  fixedDecimalScale
                />
              )}
            </form.AppField>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <form.SubmitButton>Create account</form.SubmitButton>
            </DialogFooter>
          </form.AppForm>
        </form>
      </DialogContent>
    </Dialog>
  );
}
